/**
 * Dynamic Solana priority-fee lookup for the browser.
 *
 * Returns a { cuPriceMicroLamports, tipLamports, source } tuple by asking
 * the solana-rpc-proxy edge function for `getRecentPrioritizationFees` and
 * Jito `bundles.jito.wtf/api/v1/bundles/tip_floor` for current landed-tip
 * percentiles. Urgency maps to 25th/50th/75th percentile.
 *
 * Triton primary / Alchemy fallback / circuit breaker live inside the
 * proxy edge function. The standard `getRecentPrioritizationFees` method
 * returns an array of recent-slot fees which we percentile client-side
 * (25th/50th/75th -> low/medium/high).
 *
 * On any failure (500ms timeout, non-200, malformed JSON, empty result)
 * the call returns static fallback values with `source: 'fallback'` and
 * fires a Sentry breadcrumb so we can watch the fallback rate post deploy.
 * Both lookups are in parallel; if either throws, the caller degrades as
 * a single atomic fallback.
 *
 * Results cache for 5 seconds in-module. Solana priority fees don't move
 * meaningfully in sub-second windows, and caching prevents N-trades-per-
 * second from hammering the proxy + Jito.
 */

import * as Sentry from '@sentry/react';
import { getSolanaEndpoint } from '@/lib/solana/config';

export type PriorityFeeUrgency = 'low' | 'medium' | 'high';

export interface PriorityFeeResult {
  cuPriceMicroLamports: number;
  tipLamports: number;
  source: 'rpc' | 'fallback';
}

/**
 * Static fallback values. Keep in sync with sender.ts's
 * FALLBACK_TIP_AMOUNTS + FALLBACK_COMPUTE_UNIT_PRICE. If the RPC is
 * unreachable we want behavior identical to the pre-dynamic code,
 * not degraded.
 */
export const FALLBACK_PRIORITY_FEES: Record<PriorityFeeUrgency, {
  cuPriceMicroLamports: number;
  tipLamports: number;
}> = {
  low: { cuPriceMicroLamports: 200_000, tipLamports: 200_000 },
  medium: { cuPriceMicroLamports: 200_000, tipLamports: 300_000 },
  high: { cuPriceMicroLamports: 200_000, tipLamports: 500_000 },
};

const CACHE_TTL_MS = 5_000;
const LOOKUP_TIMEOUT_MS = 500;
const JITO_TIP_FLOOR_URL = 'https://bundles.jito.wtf/api/v1/bundles/tip_floor';

type CacheEntry = {
  fetchedAt: number;
  fees: Record<PriorityFeeUrgency, { cuPriceMicroLamports: number; tipLamports: number }>;
};

let cachedEntry: CacheEntry | null = null;

/**
 * Test-only: reset the in-module cache. Not exported from the barrel.
 */
export function __resetPriorityFeeCache(): void {
  cachedEntry = null;
}

async function fetchWithTimeout(url: string, init?: RequestInit): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), LOOKUP_TIMEOUT_MS);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

async function fetchPriorityFeeLevels(): Promise<{ low: number; medium: number; high: number }> {
  const res = await fetchWithTimeout(getSolanaEndpoint(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      jsonrpc: '2.0',
      id: 'priority-fee',
      method: 'getRecentPrioritizationFees',
      params: [],
    }),
  });
  if (!res.ok) throw new Error(`rpc ${res.status}`);
  const body = await res.json();
  const fees: number[] = (Array.isArray(body?.result) ? body.result : [])
    .map((entry: { prioritizationFee?: number }) => entry?.prioritizationFee)
    .filter((f: unknown): f is number => typeof f === 'number');
  if (fees.length === 0) throw new Error('rpc empty');
  fees.sort((a, b) => a - b);
  const pick = (q: number) => fees[Math.min(fees.length - 1, Math.floor(fees.length * q))];
  return { low: pick(0.25), medium: pick(0.50), high: pick(0.75) };
}

async function fetchJitoTipPercentiles(): Promise<{ low: number; medium: number; high: number }> {
  const res = await fetchWithTimeout(JITO_TIP_FLOOR_URL);
  if (!res.ok) throw new Error(`jito ${res.status}`);
  const body = await res.json();
  const row = Array.isArray(body) ? body[0] : null;
  const p25 = row?.landed_tips_25th_percentile;
  const p50 = row?.landed_tips_50th_percentile;
  const p75 = row?.landed_tips_75th_percentile;
  if (typeof p25 !== 'number' || typeof p50 !== 'number' || typeof p75 !== 'number') {
    throw new Error('jito malformed');
  }
  // Jito tip_floor returns SOL; convert to lamports.
  return {
    low: Math.round(p25 * 1_000_000_000),
    medium: Math.round(p50 * 1_000_000_000),
    high: Math.round(p75 * 1_000_000_000),
  };
}

/**
 * Get the dynamic priority fee for the given urgency. Never throws;
 * falls back to static values with source='fallback' on any error.
 */
export async function getPriorityFee(urgency: PriorityFeeUrgency): Promise<PriorityFeeResult> {
  const now = Date.now();

  if (cachedEntry && now - cachedEntry.fetchedAt < CACHE_TTL_MS) {
    const entry = cachedEntry.fees[urgency];
    return { ...entry, source: 'rpc' };
  }

  try {
    const [rpcLevels, jito] = await Promise.all([fetchPriorityFeeLevels(), fetchJitoTipPercentiles()]);
    const merged: CacheEntry['fees'] = {
      low: {
        cuPriceMicroLamports: Math.max(FALLBACK_PRIORITY_FEES.low.cuPriceMicroLamports, Math.round(rpcLevels.low)),
        tipLamports: Math.max(FALLBACK_PRIORITY_FEES.low.tipLamports, jito.low),
      },
      medium: {
        cuPriceMicroLamports: Math.max(FALLBACK_PRIORITY_FEES.medium.cuPriceMicroLamports, Math.round(rpcLevels.medium)),
        tipLamports: Math.max(FALLBACK_PRIORITY_FEES.medium.tipLamports, jito.medium),
      },
      high: {
        cuPriceMicroLamports: Math.max(FALLBACK_PRIORITY_FEES.high.cuPriceMicroLamports, Math.round(rpcLevels.high)),
        tipLamports: Math.max(FALLBACK_PRIORITY_FEES.high.tipLamports, jito.high),
      },
    };
    cachedEntry = { fetchedAt: now, fees: merged };
    return { ...merged[urgency], source: 'rpc' };
  } catch (err) {
    const reason = err instanceof Error ? err.message : String(err);
    Sentry.addBreadcrumb({
      category: 'priority_fee',
      message: `fallback: ${reason}`,
      level: 'warning',
      data: {
        source: 'priority-fee-frontend',
        target: 'fallback',
        failover_reason: reason,
        urgency,
      },
    });
    const fb = FALLBACK_PRIORITY_FEES[urgency];
    return { ...fb, source: 'fallback' };
  }
}
